import {
  IStructureEditor,
  IStructureEditorAPI,
  IStructureRenderer,
  StructureType,
} from '@discngine/moosa-models';
import { useComponentsContext } from '@discngine/moosa-shared-components-gen';
import React, { useCallback, useEffect, useState } from 'react';

import styles from '../MoosaSarTableConfig.module.less';

import { MoosaSarConfigCoreItem } from './MoosaSarConfigCoreItem';

export interface IMoosaSarConfigCoreProps {
  structure: string[];
  onStructureChange: (structure: string[]) => void;
  StructureEditor: IStructureEditor;
  structureRenderer: IStructureRenderer;
}

export const MoosaSarConfigCoreGeneric: React.FC<IMoosaSarConfigCoreProps> = ({
  structure,
  onStructureChange,
  StructureEditor,
  structureRenderer,
}) => {
  const { Button, Modal } = useComponentsContext();

  const [isEditorVisible, setEditorVisibility] = useState(false);
  const [editedItem, setEditedItem] = useState(-1);
  const [editorApi, setEditorApi] = useState<IStructureEditorAPI>();
  const [localStruct, setLocalStruct] = useState(structure);

  const handleStructureChange = useCallback(
    (index: number, value: string | null) => {
      const newStructure = [...localStruct];

      if (!value) {
        newStructure.splice(index, 1);
      } else {
        newStructure[index] = value;
      }

      setLocalStruct(newStructure);
      onStructureChange(newStructure);
    },
    [localStruct, onStructureChange]
  );

  const handleShowEditor = useCallback((index: number) => {
    setEditorVisibility(true);
    setEditedItem(index);
  }, []);

  const handleItemAdd = useCallback(() => {
    const index = localStruct.length;

    setLocalStruct([...localStruct, '']);
    handleShowEditor(index);
  }, [handleShowEditor, localStruct]);

  const handleItemRemove = useCallback(
    (index: number) => {
      const newStructure = [...localStruct];

      newStructure.splice(index, 1);
      setLocalStruct(newStructure);
      onStructureChange(newStructure);
    },
    [localStruct, onStructureChange]
  );

  const handleOk = useCallback(() => {
    if (editorApi) {
      editorApi.getStructure(StructureType.MOLFILE).then((value: string | null) => {
        handleStructureChange(editedItem, value);
        setEditorVisibility(false);
        setEditedItem(-1);
      });
    }
  }, [editedItem, editorApi, handleStructureChange]);

  const handleCancel = useCallback(() => {
    setEditorVisibility(false);
  }, []);

  useEffect(() => {
    setLocalStruct(structure);
  }, [setLocalStruct, structure]);

  return (
    <>
      {localStruct.length === 0 && <h3>Core</h3>}

      <div>
        {localStruct.map((structureString, index) => (
          <MoosaSarConfigCoreItem
            key={index}
            index={index}
            structure={structureString}
            StructureRenderer={structureRenderer}
            onRemove={handleItemRemove}
            onShowEditor={handleShowEditor}
          />
        ))}

        <Button className={styles.button} onClick={handleItemAdd}>
          {localStruct.length === 0 && <span>Add core</span>}
          {localStruct.length > 0 && <span>Add another core</span>}
        </Button>
      </div>
      <Modal
        open={isEditorVisible}
        // title="Structure Editor" // TODO:
        width={1280}
        onCancel={handleCancel}
        onOk={handleOk}
      >
        <div style={{ overflow: 'hidden' }}>
          <StructureEditor
            structure={localStruct[editedItem] ?? ''}
            onInit={setEditorApi}
          />
        </div>
      </Modal>
    </>
  );
};
